// apps/spa/src/hooks/useToggleableRoles.ts
// TanStack Query hooks for /api/toggleable-roles CRUD. Follows useCommands pattern.

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import type { CreateToggleableRoleBody, UpdateToggleableRoleBody } from '@guardian/shared';

export interface ToggleableRole {
  guildId: string;
  roleId: string;
  label: string;
  position: number | null;
  createdAt: number;
}

const KEY = ['toggleable-roles'] as const;

export function useToggleableRoles() {
  return useQuery<ToggleableRole[]>({
    queryKey: KEY,
    queryFn: () => api<ToggleableRole[]>('/api/toggleable-roles'),
  });
}

export function useCreateToggleableRole() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: CreateToggleableRoleBody) =>
      api<ToggleableRole>('/api/toggleable-roles', {
        method: 'POST',
        body: JSON.stringify(body),
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  });
}

export function useUpdateToggleableRole() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ roleId, body }: { roleId: string; body: UpdateToggleableRoleBody }) =>
      api<ToggleableRole>(`/api/toggleable-roles/${roleId}`, {
        method: 'PATCH',
        body: JSON.stringify(body),
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  });
}

export function useDeleteToggleableRole() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (roleId: string) =>
      api<void>(`/api/toggleable-roles/${roleId}`, { method: 'DELETE' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  });
}
